import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { GridExternalMessageResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/GridExternalMessageResponse";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { ExternalMessageInbox } from "@/Common/Models/ExternalCommunications/GridExternalMessage";
import { CommunicationsUIStateInbox, ExternalCommunicationsSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalMessageInboxTableItem } from "@/Communications/Components/ExternalCommunications/ExternalMessageInboxTableItem";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { ExternalMessageInboxSettingsContainer, ExternalMessageInboxSettingsContainerState } from "./ExternalMessageInboxSettingsContainer";

interface ExternalMessageInboxProps {
    gridSessionId: string;
    communicationsCount?: () => void;
    onMessageRowClick: (message: ExternalMessageInbox) => void;
    savedState: ExternalMessageInboxState;
    inboxType: CommunicationsUIStateInbox;
}

class ExternalMessageInboxState {
    public messages: ExternalMessageInboxTableItem[];
    public isAllMessagesChecked: boolean;
    public isSettingsContainerDisplayed: boolean;
    public settings: ExternalMessageInboxSettingsContainerState;

    public searchText: string;
    public currentPageNumber: number;
    public totalQueryPages: number;
    public totalQueryRows: number;
    public selectedPageNumber: number;

    public sortingColumn: ExternalCommunicationsSortOrderType;
    public sortingDirection: SortDirection;

    public static getDefault(): ExternalMessageInboxState {
        const state = new ExternalMessageInboxState();
        state.messages = [];
        state.isAllMessagesChecked = false;
        state.searchText = "";
        state.currentPageNumber = 1;
        state.selectedPageNumber = 1;
        state.totalQueryPages = 0;
        state.totalQueryRows = 0;
        state.isSettingsContainerDisplayed = false;
        state.settings = ExternalMessageInboxSettingsContainerState.createDefault();

        return state;
    }
}

class ExternalMessageInboxComponent extends BaseReactComponent<ExternalMessageInboxProps, ExternalMessageInboxState> {
    private _saveOptionKey: string;
    private _viewName: string = cacheStore.GetString("InboxLabel");

    private _unreadMessageStyle = { fontWeight: "bold" } as React.CSSProperties;

    constructor(props: ExternalMessageInboxProps) {
        super(props);

        this.state = this.props.savedState || ExternalMessageInboxState.getDefault();
    }

    public componentDidMount(): void {
        this.loadMessages();
        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: ExternalMessageInboxProps): void {
        if (this.props.gridSessionId && this.props.gridSessionId !== prevProps.gridSessionId) {
            this.loadMessages(this.props.inboxType);
        }
    }

    public componentWillUnmount(): void {
        communicationsStore.saveExternalMessageInboxSearchState(this.state);
    }

    public render(): JSX.Element {
        this.setClassParameters();

        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this._saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={`${cacheStore.GetString("ExternalCommunicationsLabel")} : ${this._viewName}`}
                    searchText={this.state.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInMessagesLabel")}
                    onSearch={text => this.mutateState(s => s.searchText = text, () => this.loadMessages())}
                    onRefreshClick={() => this.loadMessages()}
                    onSettingsClick={() => this.mutateState(s => s.isSettingsContainerDisplayed = !this.state.isSettingsContainerDisplayed)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.state.isSettingsContainerDisplayed && (
                        <ExternalMessageInboxSettingsContainer
                            onCancelClick={() => this.mutateState(s => s.isSettingsContainerDisplayed = false)}
                            onSave={(settings: ExternalMessageInboxSettingsContainerState) => this.handleSettingsSave(settings)}
                            saveState={this.state.settings}
                        />
                    )}
                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_message_table"
                            Columns={tableColumns}
                            DataSource={this.state.messages.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            SaveOptionskeyTarget={this._saveOptionKey}
                            RowClickFunction={messageItem => { this.props.onMessageRowClick(messageItem.Model); }}
                            ColumnReorderTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnSortClick}
                        />
                    </div>
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                        <SimplePagerControl
                            PageSize={this.state.settings.selectedPageSizeOption}
                            TotalRows={this.state.totalQueryRows}
                            CurrentPage={this.state.currentPageNumber}
                            changePageFunction={pageNumber => this.mutateState(s => s.selectedPageNumber = pageNumber, () => this.loadMessages())}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                        {this.props.inboxType === CommunicationsUIStateInbox.Inbox && (
                            <button
                                disabled={!(this.state.messages ?? []).filter(p => p.IsSelected).any()}
                                className="btn btn-danger push-right-5"
                                onClick={() => this.changeMessageDeletionState(ServerOperations.Server.Myle_ExternalCommunication_DeleteInboxMessages)}
                            >
                                {cacheStore.GetString("DeleteLabel")}
                            </button>
                        )}
                        {this.props.inboxType === CommunicationsUIStateInbox.Trash && (
                            <button
                                disabled={!(this.state.messages ?? []).filter(p => p.IsSelected).any()}
                                className="btn btn-primary"
                                onClick={() => this.changeMessageDeletionState(ServerOperations.Server.Myle_ExternalCommunication_RestoreInboxMessages)}
                            >
                                {cacheStore.GetString("RestoreLabel")}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private setClassParameters(): void {
        switch (this.props.inboxType) {
            case CommunicationsUIStateInbox.Inbox:
                this._viewName = cacheStore.GetString("InboxLabel");
                break;
            case CommunicationsUIStateInbox.Trash:
                this._viewName = cacheStore.GetString("TrashLabel");
                break;
            default:
                this._viewName = "";
        }

        this._saveOptionKey = "ExternalMessageInbox";
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "Checkbox",
                WidthPixels: 38,
                IsFixedInPlace: true,
                FloatLeft: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubjectLabel"),
                FieldName: "Subject",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.Subject,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.Subject)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("FromLabel"),
                FieldName: "From",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.From,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 20,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.From)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ToLabel"),
                FieldName: "To",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.To,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.To)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ReceivedDateLabel"),
                FieldName: "ReceivedDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.ReceivedDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.ReceivedDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "Patient",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: ExternalCommunicationsSortOrderType.Patient,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(ExternalCommunicationsSortOrderType.Patient)
            })
        ];
    }

    private sortArrayBy(column: ExternalCommunicationsSortOrderType): void {
        this.mutateState(s => {
            s.sortingColumn = column;
            s.sortingDirection = MyleTableHelper.getNewSortDir(column, s.sortingColumn, s.sortingDirection);
            s.messages = s.messages.sortByProperty(
                communicationItem => {
                    let message = communicationItem.Model;

                    switch (column) {
                        case ExternalCommunicationsSortOrderType.Subject:
                            return (message.subject || "").toUpperCase();

                        case ExternalCommunicationsSortOrderType.From:
                            let senderDisplayName = message.senderDisplayName;
                            if (!IsNullOrWhiteSpace(senderDisplayName))
                                return senderDisplayName;

                            let fromEmployeeId = message.senderInternalEmployeeId;

                            if (fromEmployeeId && cacheStore.GetCliniciansPersonsByContactFacetId(fromEmployeeId))
                                return cacheStore.GetCliniciansPersonsByContactFacetId(fromEmployeeId).LastName;

                        case ExternalCommunicationsSortOrderType.To:
                            if (!IsNullOrWhiteSpace(message.receiverInternalEmployeeId))
                                return message.receiverDisplayName;

                            let toEmployeeId = message.receiverInternalEmployeeId;

                            if (toEmployeeId && cacheStore.GetCliniciansPersonsByContactFacetId(toEmployeeId))
                                return cacheStore.GetCliniciansPersonsByContactFacetId(toEmployeeId).LastName;

                        case ExternalCommunicationsSortOrderType.ReceivedDate:
                            return message.receivedDate;

                        case ExternalCommunicationsSortOrderType.Patient:
                            return message.patientDisplayName;

                        default:
                            return message.receivedDate;
                    }
                },
                this.state.sortingDirection === SortDirection.DESC
            );
        });
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<ExternalMessageInboxTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "Checkbox":
                        return this.buildCheckboxColumn();

                    case "Subject":
                        return this.buildSubjectColumn();

                    case "From":
                        return this.buildFromColumn();

                    case "To":
                        return this.buildToColumn();

                    case "ReceivedDate":
                        return this.buildReceivedDateColumn();

                    case "Patient":
                        return this.buildPatientColumn();
                }
            },
            false
        );
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private buildCheckboxColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: item => (
                <div>
                    <input
                        type="checkbox"
                        id={item.Model.messageId}
                        checked={item.IsSelected}
                        onChange={() => this.onMessageCheck(item)}
                    />
                </div>
            ),
            renderHeader: () => (
                <div>
                    <input
                        type="checkbox"
                        onChange={() => this.onAllMessagesChecked()}
                        checked={this.state.isAllMessagesChecked}
                    />
                </div>
            )
        };
    }

    private buildSubjectColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.subject, item)
        };
    }

    private buildFromColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.senderDisplayName, item)
        };
    }

    private buildToColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: item => (
                <div style={item.Model.isRead ? {} : this._unreadMessageStyle}>{item.Model.receiverDisplayName}</div>
            )
        };
    }

    private buildReceivedDateColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: item => {
                if (!item.Model.receivedDate)
                    return <div />;

                const date = moment.utc(item.Model.receivedDate).local().format("YYYY/MM/DD");
                const time = moment.utc(item.Model.receivedDate).local().format("HH:mm:ss");
                const messageDate = `${date} ${cacheStore.GetString("AtLabel")} ${time}`;

                return this.renderRowCommon(messageDate, item);
            }
        };
    }

    private buildPatientColumn(): MyleTableColumnInit<ExternalMessageInboxTableItem> {
        return {
            renderCell: (item: ExternalMessageInboxTableItem) => {
                if (item.Model.internalPatientId)
                    return (
                        <div key="patientLink">
                            <HyperLinkRedirectControl
                                target={RedirectTo.Patient}
                                redirectValue={item.Model.internalPatientId}
                                text={item.Model.patientDisplayName}
                            />
                        </div>
                    );

                return <div style={item.Model.isRead ? null : this._unreadMessageStyle} key="patientLink">{item.Model.patientDisplayName}</div>;
            }
        };
    }

    private renderRowCommon(text: string, item: ExternalMessageInboxTableItem): JSX.Element {
        return item.Model.isRead
            ? <div>{text}</div>
            : <div style={this._unreadMessageStyle}>{text}</div>;
    }

    private onAllMessagesChecked(): void {
        this.mutateState(s => {
            s.messages.forEach(c => c.IsSelected = !this.state.isAllMessagesChecked);
            s.isAllMessagesChecked = !this.state.isAllMessagesChecked;
        });
    }

    private onMessageCheck(item: ExternalMessageInboxTableItem): void {
        this.mutateState(s => s.messages.mutate(x => x.IsSelected = !item.IsSelected, x => item.Model.messageId === x.Model.messageId));
    }

    private loadMessages(inboxTypeOverride: CommunicationsUIStateInbox = null): void {
        const inboxType = inboxTypeOverride ?? this.props.inboxType;
        let requestData = {
            PageSize: this.state.settings.selectedPageSizeOption,
            PageNumber: this.state.selectedPageNumber,
            ReceivedDateFrom: this.state.settings.receivedDateFrom,
            ReceivedDateTo: this.state.settings.receivedDateTo,
            SearchText: this.state.searchText,
            SharedWithContactFacetIdList: this.state.settings.sharedWithList.filter(x => x.Checked).map(x => x.ContactFacetId),
            IsTrash: inboxType === CommunicationsUIStateInbox.Trash
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_LoadInboxMessages,
            requestData,
            response => {
                if (response) {
                    let externalMessageResponse: GridExternalMessageResponse[] = response.Messages;

                    this.mutateState(s => {
                        s.currentPageNumber = response.CurrentPage;
                        s.totalQueryPages = response.TotalQueryPages;
                        s.totalQueryRows = response.TotalQueryRows;
                        s.messages = externalMessageResponse.map(
                            x => new ExternalMessageInboxTableItem(new ExternalMessageInbox(x))
                        );
                    });
                }
            }
        );
    }

    private async changeMessageDeletionState(operationNumber: ServerOperations.Server): Promise<void> {
        const messageIds = this.state.messages.filter(p => p.IsSelected).map(p => p.Model.messageId);

        const response = await Client.postAsync<null>(operationNumber, { MessageIds: messageIds });

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        this.mutateState(s => s.isAllMessagesChecked = false, () => {
            this.loadMessages();
            this.props.communicationsCount?.();
        });
    }

    private handleSettingsSave(settings: ExternalMessageInboxSettingsContainerState): void {
        this.mutateState(
            s => {
                if (this.shouldResetToFirstPage(settings))
                    s.selectedPageNumber = 1;

                s.isSettingsContainerDisplayed = false;
                s.settings = settings;
            },
            () => this.loadMessages()
        );
    }

    private shouldResetToFirstPage(settings: ExternalMessageInboxSettingsContainerState): boolean {
        return this.state.settings.selectedPageSizeOption !== settings.selectedPageSizeOption;
    }
}

export { ExternalMessageInboxComponent, ExternalMessageInboxProps, ExternalMessageInboxState };
